import { ConnectorTemplate, ConnectorFieldMapping } from "../../types"

/**
 * Predefined connector templates for common CRM integrations
 * These provide smart defaults that users can customize
 */

// Lead field mappings
export const ZAPIER_LEAD_MAPPING: ConnectorFieldMapping = {
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },
  loan_purpose: { key: "details.loan_purpose" },
  loan_type: { key: "details.loan_type" },
  loan_amount: { key: "details.loan_amount" },
  credit_score: { key: "details.credit_score" },
  city: { key: "details.location.city" },
  state: { key: "details.location.state" },
  zipcode: { key: "details.location.zipcode" },
  message: { key: "details.message" }
}

export const GENERIC_LEAD_MAPPING: ConnectorFieldMapping = {
  firstname: { key: "first_name" },
  lastname: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },
  creditscore: { key: "details.credit_score" },
  downpayment: { key: "details.down_payment" },
  listprice: { key: "details.list_price" },
  loanamount: { key: "details.loan_amount" },
  loanpurpose: { key: "details.loan_purpose" },
  loantype: { key: "details.loan_type" },
  loanterm: { key: "details.loan_term" },
  propertytype: { key: "details.property_type" },
  residencytype: { key: "details.residency_type" },
  realtor: { key: "details.realtor" },
  city: { key: "details.location.city" },
  state: { key: "details.location.state" },
  zipcode: { key: "details.location.zipcode" },
  streetaddress: { key: "details.location.street_address" },
  county: { key: "details.location.county" },
  message: { key: "details.message" }
}

// Subscription field mappings
export const ZAPIER_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  phone: { key: "phone" },
  email: { key: "email" },
  loid: { key: "loid" },
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  loan_purpose: { key: "quote_info.loan_purpose" },
  credit_score: { key: "quote_info.credit_score" },
  property_type: { key: "quote_info.property_type" },
  residency_type: { key: "quote_info.residency_type" },
  list_price: { key: "quote_info.list_price" },
  loan_amount: { key: "quote_info.loan_amount" },
  loan_term: { key: "quote_info.loan_term" },
  loan_type: { key: "quote_info.loan_type" },
  state: { key: "quote_info.location.state" },
  city: { key: "quote_info.location.city" },
  zipcode: { key: "quote_info.location.zipcode" },
  street_address: { key: "quote_info.location.street_address" },
  county: { key: "quote_info.location.county" }
}

// Pending subscription field mappings
export const ZAPIER_PENDING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  phone: { key: "phone" },
  email: { key: "email" },
  loid: { key: "loid" },
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  trigger_rate: { key: "trigger_rate" },
  loan_purpose: { key: "quote_info.loan_purpose" },
  credit_score: { key: "quote_info.credit_score" },
  property_type: { key: "quote_info.property_type" },
  residency_type: { key: "quote_info.residency_type" },
  list_price: { key: "quote_info.list_price" },
  loan_amount: { key: "quote_info.loan_amount" },
  loan_term: { key: "quote_info.loan_term" },
  loan_type: { key: "quote_info.loan_type" },
  state: { key: "quote_info.location.state" },
  city: { key: "quote_info.location.city" },
  zipcode: { key: "quote_info.location.zipcode" },
  street_address: { key: "quote_info.location.street_address" },
  county: { key: "quote_info.location.county" }
}

// Own Up incoming lead mapping (for receiving leads from Own Up)
export const OWNUP_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = {
  leadID: {
    key: "details.source_lead_id"
  },
  leadCost: {
    key: "details.lead_cost"
  },
  subsource: {
    key: "details.subsource"
  },
  firstName: {
    key: "first_name"
  },
  lastName: {
    key: "last_name"
  },
  email: {
    key: "email"
  },
  phoneNumber: {
    key: "phone"
  },
  creditRating: {
    key: "details.credit_score",
    values: {
      Excellent: 800,
      Good: 700,
      Fair: 650,
      Poor: 600,
      "Very Poor": 550,
      default: 650
    }
  },
  vaEligible: {
    key: "details.va_eligible"
  },
  fthb: {
    key: "details.first_time_buyer"
  },
  bankruptcy: {
    key: "details.foreclosure_bankruptcy"
  },
  hasAgent: {
    key: "details.has_agent"
  },
  hasCoborrower: {
    key: "details.has_coborrower"
  },
  incomeType: {
    key: "details.income_type",
    values: {
      Salary: "salary",
      Hourly: "hourly",
      Bonus: "bonus",
      Commission: "commission",
      Pension: "pension",
      SelfEmployed: "self_employed",
      NoIncome: "no_income",
      Other: "other",
      default: "salary"
    }
  },
  grossIncome: {
    key: "details.household_income"
  },
  propertyStreet: {
    key: "details.location.street_address"
  },
  propertyCity: {
    key: "details.location.city"
  },
  propertyState: {
    key: "details.location.state"
  },
  propertyCounty: {
    key: "details.location.county"
  },
  propertyZip: {
    key: "details.location.zipcode"
  },
  propertyValue: {
    key: "details.list_price"
  },
  propertyType: {
    key: "details.property_type",
    values: {
      SingleFamily: "single_family_home",
      MultiFamily: "multi_tenant_home",
      Condo: "condominium",
      Manufactured: "single_family_home",
      Townhome: "townhome",
      NewConstruction: "single_family_home",
      Mobile: "single_family_home",
      default: "single_family_home"
    }
  },
  propertyUse: {
    key: "details.residency_type",
    values: {
      Primary: "primary_home",
      Secondary: "second_home",
      Investment: "rental_home",
      default: "primary_home"
    }
  },
  loanPurpose: {
    key: "details.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      default: "purchase"
    }
  },
  loanAmount: {
    key: "details.loan_amount"
  },
  hasAcceptedOffer: {
    key: "details.has_accepted_offer"
  },
  cashOutAmount: {
    key: "details.cashout_amount"
  },
  currentMortgageBalance: {
    key: "details.current_mortgage_balance"
  },
  currentMortgageRate: {
    key: "details.current_mortgage_rate"
  },
  jornayaLeadId: {
    key: "details.jornaya_lead_id"
  },
  trustedFormCertificateUrl: {
    key: "details.trusted_form_url"
  },
  source: {
    key: "details.lead_source"
  },
  sourceId: {
    key: "details.source_id"
  },
  anonymousId: {
    key: "details.anonymous_id"
  },
  sourceChannel: {
    key: "details.source_channel"
  },
  leadPricePaid: {
    key: "details.lead_price_paid"
  },
  dateLeadReceived: {
    key: "details.date_lead_received"
  },
  exclusive: {
    key: "details.exclusive"
  },
  coborrowerEmail: {
    key: "details.coborrower_email"
  },
  hasPreapproval: {
    key: "details.has_preapproval"
  },
  questionnaireStartTime: {
    key: "details.questionnaire_start_time"
  },
  questionnaireCompletionTime: {
    key: "details.questionnaire_completion_time"
  },
  purchaseTimeline: {
    key: "details.purchase_timeline_months"
  },
  closeDate: {
    key: "details.close_date"
  },
  accountId: {
    key: "details.account_id"
  },
  advisor: {
    key: "details.advisor_email"
  },
  hubspotContactId: {
    key: "details.hubspot_contact_id"
  },
  hubspotDealId: {
    key: "details.hubspot_deal_id"
  },
  loanType: {
    key: "details.loan_type",
    values: {
      Conventional: "conventional",
      "Homeready/Homepossible": "homeready",
      FHA: "fha",
      HELOC: "heloc",
      Reverse: "reverse",
      USDA: "usda",
      VA: "va",
      Jumbo: "jumbo",
      "Non QM": "non_qm",
      DPA: "dpa",
      Construction: "construction",
      Rehab: "rehab",
      Renovation: "renovation",
      default: "conventional"
    }
  },
  campaign: {
    key: "details.campaign"
  },
  campaignId: {
    key: "details.campaign_id"
  },
  comments: {
    key: "details.message"
  },
  currentProductType: {
    key: "details.current_product_type",
    values: {
      Fixed30: "fixed_30",
      Fixed20: "fixed_20",
      Fixed15: "fixed_15",
      Fixed10: "fixed_10",
      ARM10: "arm_10",
      ARM7: "arm_7",
      ARM5: "arm_5",
      Other: "other",
      default: "fixed_30"
    }
  },
  leadType: {
    key: "details.lead_type",
    values: {
      "web-lead": "web_lead",
      "direct-web-lead": "direct_web_lead",
      "preapproval-lead": "preapproval_lead",
      "preapproval-lead-tier-2": "preapproval_lead_tier_2",
      "rate-quote-lead": "rate_quote_lead",
      "click-out-lead": "click_out_lead",
      "rate-table-lead": "rate_table_lead",
      "recapture-from-concierge-lead": "recapture_lead",
      default: "web_lead"
    }
  },
  ownOrRent: {
    key: "details.own_or_rent",
    values: {
      Own: "own",
      Rent: "rent",
      default: "own"
    }
  },
  timeline: {
    key: "details.timeline",
    values: {
      "0-3Months": "0_3_months",
      "3-6Months": "3_6_months",
      "6+Months": "6_plus_months",
      default: "3_6_months"
    }
  },
  questionnaireType: {
    key: "details.questionnaire_type",
    values: {
      purchase: "purchase",
      preapproval: "preapproval",
      refinance: "refinance",
      "accepted-offer": "accepted_offer",
      default: "purchase"
    }
  },
  downPayment: {
    key: "details.down_payment"
  },
  employmentStatus: {
    key: "details.employment_status",
    values: {
      Employed: "employed",
      SelfEmployed: "self_employed",
      Retired: "retired",
      Unemployed: "unemployed",
      default: "employed"
    }
  },
  state: {
    key: "details.shopping_state"
  }
}

// MRC incoming lead mapping (for receiving leads from Mortgage Research Center)
export const MRC_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = {
  Internal_LeadID: {
    key: "details.source_lead_id"
  },
  source: {
    key: "details.lead_source"
  },
  lead_price_paid: {
    key: "details.lead_cost"
  },
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  phone: { key: "phone" },
  phone_home: {
    key: "details.phone_home"
  },
  phone_cell: {
    key: "phone"
  },
  phone_work: {
    key: "details.phone_work"
  },
  address: {
    key: "details.current_address"
  },
  address2: {
    key: "details.current_address2"
  },
  city: {
    key: "details.current_city"
  },
  state: {
    key: "details.current_state"
  },
  zip_code: {
    key: "details.current_zipcode"
  },
  county: {
    key: "details.current_county"
  },
  country: {
    key: "details.country"
  },
  ip_address: {
    key: "details.ip_address"
  },
  current_rate: {
    key: "details.current_mortgage_rate"
  },
  living_situation: {
    key: "details.living_situation",
    values: {
      own: "own",
      rent: "rent",
      other: "other",
      default: "own"
    }
  },
  military_status: {
    key: "details.military_status",
    values: {
      active: "active",
      veteran: "veteran",
      reserves: "reserves",
      spouse: "spouse",
      none: "none",
      default: "none"
    }
  },
  credit_rating: {
    key: "details.credit_score",
    values: {
      Excellent: 800,
      Good: 720,
      Fair: 660,
      Poor: 600,
      Bad: 550,
      default: 660
    }
  },
  email: {
    key: "email"
  },
  bankruptcy: {
    key: "details.foreclosure_bankruptcy",
    values: {
      N: "no",
      Y: "yes",
      "2year": "2_years",
      "5year": "5_years",
      "7year": "7_years",
      default: "no"
    }
  },
  leadid_token: {
    key: "details.jornaya_lead_id"
  },
  loan_product: {
    key: "details.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  loan_purpose: {
    key: "details.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      default: "purchase"
    }
  },
  military: {
    key: "details.military_eligible",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  military_branch: {
    key: "details.military_branch",
    values: {
      Army: "army",
      Marine: "marine",
      Navy: "navy",
      Air: "air_force",
      Coast: "coast_guard",
      National: "national_guard",
      Spouse: "spouse",
      Other: "other",
      Civilian: "civilian",
      default: "civilian"
    }
  },
  property_state: {
    key: "details.location.state"
  },
  property_type: {
    key: "details.property_type",
    values: {
      single: "single_family_home",
      condominium: "condominium",
      townhome: "townhome",
      multifamily: "multi_tenant_home",
      other: "single_family_home",
      manufactured: "single_family_home",
      "new construction": "single_family_home",
      default: "single_family_home"
    }
  },
  property_value: {
    key: "details.list_price"
  },
  property_zip: {
    key: "details.location.zipcode"
  },
  real_estate_agent: {
    key: "details.has_agent",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  refi_balance: {
    key: "details.current_mortgage_balance"
  },
  refi_value: {
    key: "details.refi_property_value"
  },
  when_loan: {
    key: "details.purchase_readiness",
    values: {
      "signed purchase agreement": "considering_loan_offers_now",
      "offer pending": "considering_loan_offers_now",
      "within a month": "thirty_days_out",
      "2 to 3 months": "two_to_four_months",
      "3 to 6 months": "two_to_four_months",
      "over 6 months": "researching_options",
      "no time frame": "researching_options",
      now: "considering_loan_offers_now",
      soon: "thirty_days_out",
      someday: "researching_options",
      default: "two_to_four_months"
    }
  },
  refi_reason: {
    key: "details.refi_reason",
    values: {
      rate: "lower_rate",
      home_improvements: "home_improvements",
      payoff_debt: "payoff_debt",
      cashout: "cashout",
      consolidation: "consolidation",
      other: "other",
      lower_payments: "lower_payments",
      payoff_faster: "payoff_faster",
      default: "lower_rate"
    }
  },
  self_employed: {
    key: "details.self_employed",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  purchase_first: {
    key: "details.first_time_buyer",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  manufactured_own: {
    key: "details.manufactured_land_ownership",
    values: {
      own: "own",
      rent: "rent",
      default: "own"
    }
  },
  down_payment_pct: {
    key: "details.down_payment_percent"
  },
  property_url: {
    key: "details.property_url"
  },
  gross_income: {
    key: "details.household_income"
  },
  employment_status: {
    key: "details.employment_status",
    values: {
      employed: "employed",
      "self employed": "self_employed",
      retired: "retired",
      "not employed": "unemployed",
      default: "employed"
    }
  },
  property_city: {
    key: "details.location.city"
  },
  loan_term: {
    key: "details.loan_term"
  },
  addr_city: {
    key: "details.current_city"
  },
  addr_state: {
    key: "details.current_state"
  },
  down_payment: {
    key: "details.down_payment"
  },
  property_use: {
    key: "details.residency_type",
    values: {
      primary: "primary_home",
      secondary: "second_home",
      investment: "rental_home",
      default: "primary_home"
    }
  },
  property_county: {
    key: "details.location.county"
  },
  refi_current_loan_type: {
    key: "details.current_loan_type"
  },
  cash_outamt: {
    key: "details.cashout_amount"
  }
}

// MRC incoming subscription mapping (for receiving subscriptions from Mortgage Research Center)
export const MRC_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },
  phone_cell: { key: "phone" },
  loid: { key: "loid" },
  credit_rating: {
    key: "quote_info.credit_score",
    values: {
      Excellent: 800,
      Good: 720,
      Fair: 660,
      Poor: 600,
      Bad: 550,
      default: 660
    }
  },
  loan_product: {
    key: "quote_info.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  loan_purpose: {
    key: "quote_info.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      default: "purchase"
    }
  },
  loan_amount: { key: "quote_info.loan_amount" },
  loan_term: { key: "quote_info.loan_term" },
  property_value: { key: "quote_info.list_price" },
  property_type: {
    key: "quote_info.property_type",
    values: {
      single: "single_family_home",
      condominium: "condominium",
      townhome: "townhome",
      multifamily: "multi_tenant_home",
      other: "single_family_home",
      manufactured: "single_family_home",
      "new construction": "single_family_home",
      default: "single_family_home"
    }
  },
  property_use: {
    key: "quote_info.residency_type",
    values: {
      primary: "primary_home",
      secondary: "second_home",
      investment: "rental_home",
      default: "primary_home"
    }
  },
  property_state: { key: "quote_info.location.state" },
  property_city: { key: "quote_info.location.city" },
  property_zip: { key: "quote_info.location.zipcode" },
  property_county: { key: "quote_info.location.county" }
}

// Generic incoming subscription mapping
export const GENERIC_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },
  loid: { key: "loid" },
  loan_purpose: {
    key: "quote_info.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      purchase: "purchase",
      refinance: "refinance",
      default: "purchase"
    }
  },
  loan_type: {
    key: "quote_info.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  loan_amount: { key: "quote_info.loan_amount" },
  loan_term: { key: "quote_info.loan_term" },
  list_price: { key: "quote_info.list_price" },
  credit_score: { key: "quote_info.credit_score" },
  property_type: {
    key: "quote_info.property_type",
    values: {
      single: "single_family_home",
      single_family: "single_family_home",
      condominium: "condominium",
      condo: "condominium",
      townhome: "townhome",
      townhouse: "townhome",
      multifamily: "multi_tenant_home",
      multi_family: "multi_tenant_home",
      default: "single_family_home"
    }
  },
  residency_type: {
    key: "quote_info.residency_type",
    values: {
      primary: "primary_home",
      primary_home: "primary_home",
      secondary: "second_home",
      second_home: "second_home",
      investment: "rental_home",
      rental: "rental_home",
      default: "primary_home"
    }
  },
  state: { key: "quote_info.location.state" },
  city: { key: "quote_info.location.city" },
  zipcode: { key: "quote_info.location.zipcode" },
  zip: { key: "quote_info.location.zipcode" },
  county: { key: "quote_info.location.county" },
  street_address: { key: "quote_info.location.street_address" }
}

// Template definitions
export const CONNECTOR_TEMPLATES: ConnectorTemplate[] = [
  {
    name: "Zapier - Lead (Standard)",
    type: "zapier",
    data_type: "lead",
    field_mapping: ZAPIER_LEAD_MAPPING,
    description:
      "Standard field mapping for Zapier lead webhooks with camelCase field names"
  },
  {
    name: "Generic Webhook - Lead (Lowercase)",
    type: "webhook",
    data_type: "lead",
    field_mapping: GENERIC_LEAD_MAPPING,
    description:
      "Generic webhook mapping for leads with lowercase, no-underscore field names (common in many CRMs)"
  },
  {
    name: "Own Up - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: OWNUP_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for Own Up mortgage marketplace with comprehensive borrower and property data"
  },
  {
    name: "MRC - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: MRC_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for MRC (Mortgage Research Center) with comprehensive borrower, property, and refinance data"
  },
  {
    name: "Zapier - Subscription",
    type: "zapier",
    data_type: "subscription",
    field_mapping: ZAPIER_SUBSCRIPTION_MAPPING,
    description: "Standard field mapping for Zapier subscription webhooks"
  },
  {
    name: "Generic - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: GENERIC_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Generic incoming subscription mapping with value transformations for common field formats"
  },
  {
    name: "MRC - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: MRC_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for MRC (Mortgage Research Center) with property and loan data"
  },
  {
    name: "Zapier - Pending Subscription",
    type: "zapier",
    data_type: "pending_subscription",
    field_mapping: ZAPIER_PENDING_SUBSCRIPTION_MAPPING,
    description:
      "Standard field mapping for Zapier pending subscription webhooks"
  }
]

/**
 * Get available templates filtered by data type
 */
export const getTemplatesByDataType = (
  dataType: "lead" | "subscription" | "pending_subscription"
): ConnectorTemplate[] => {
  return CONNECTOR_TEMPLATES.filter(t => t.data_type === dataType)
}

/**
 * Get template by name
 */
export const getTemplateByName = (
  name: string
): ConnectorTemplate | undefined => {
  return CONNECTOR_TEMPLATES.find(t => t.name === name)
}

/**
 * Get default template for a given CRM type and data type
 */
export const getDefaultTemplate = (
  crmType: string,
  dataType: "lead" | "subscription" | "pending_subscription"
): ConnectorTemplate | undefined => {
  return CONNECTOR_TEMPLATES.find(
    t => t.type === crmType && t.data_type === dataType
  )
}

// Field reference documentation for help text
export const LEAD_FIELD_OPTIONS = [
  { value: "email", label: "Email", category: "Basic" },
  { value: "phone", label: "Phone", category: "Basic" },
  { value: "first_name", label: "First Name", category: "Basic" },
  { value: "last_name", label: "Last Name", category: "Basic" },
  {
    value: "details.loan_purpose",
    label: "Loan Purpose",
    category: "Loan Info"
  },
  { value: "details.loan_type", label: "Loan Type", category: "Loan Info" },
  { value: "details.loan_amount", label: "Loan Amount", category: "Loan Info" },
  { value: "details.loan_term", label: "Loan Term", category: "Loan Info" },
  {
    value: "details.credit_score",
    label: "Credit Score",
    category: "Loan Info"
  },
  {
    value: "details.down_payment",
    label: "Down Payment",
    category: "Loan Info"
  },
  { value: "details.list_price", label: "List Price", category: "Loan Info" },
  {
    value: "details.property_type",
    label: "Property Type",
    category: "Property"
  },
  {
    value: "details.residency_type",
    label: "Residency Type",
    category: "Property"
  },
  { value: "details.realtor", label: "Realtor", category: "Property" },
  { value: "details.location.city", label: "City", category: "Location" },
  { value: "details.location.state", label: "State", category: "Location" },
  {
    value: "details.location.zipcode",
    label: "Zip Code",
    category: "Location"
  },
  {
    value: "details.location.street_address",
    label: "Street Address",
    category: "Location"
  },
  { value: "details.location.county", label: "County", category: "Location" },
  { value: "details.message", label: "Message", category: "Other" }
]

export const SUBSCRIPTION_FIELD_OPTIONS = [
  { value: "email", label: "Email", category: "Basic" },
  { value: "phone", label: "Phone", category: "Basic" },
  { value: "first_name", label: "First Name", category: "Basic" },
  { value: "last_name", label: "Last Name", category: "Basic" },
  { value: "loid", label: "Loan Officer ID", category: "Basic" },
  { value: "type", label: "Type", category: "Basic" },
  { value: "active", label: "Active", category: "Basic" },
  {
    value: "quote_info.loan_purpose",
    label: "Loan Purpose",
    category: "Quote Info"
  },
  {
    value: "quote_info.credit_score",
    label: "Credit Score",
    category: "Quote Info"
  },
  {
    value: "quote_info.property_type",
    label: "Property Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.residency_type",
    label: "Residency Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.list_price",
    label: "List Price",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_amount",
    label: "Loan Amount",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_term",
    label: "Loan Term",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_type",
    label: "Loan Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.location.state",
    label: "State",
    category: "Location"
  },
  { value: "quote_info.location.city", label: "City", category: "Location" },
  {
    value: "quote_info.location.zipcode",
    label: "Zip Code",
    category: "Location"
  },
  {
    value: "quote_info.location.street_address",
    label: "Street Address",
    category: "Location"
  },
  {
    value: "quote_info.location.county",
    label: "County",
    category: "Location"
  }
]

export const PENDING_SUBSCRIPTION_FIELD_OPTIONS = [
  ...SUBSCRIPTION_FIELD_OPTIONS,
  { value: "trigger_rate", label: "Trigger Rate", category: "Basic" }
]
